import { useState } from 'react';
import { getMyMysteryBoxList } from '@/service/service-renewal';
import type { MyMysteryBoxItem, MyMysteryBoxPagination } from './types';

interface FetchParams {
    page?: number;
    size?: number;
    userId?: number;
    campaignId?: number;
}

interface UseMyMysteryBoxReturn {
    loading: boolean;
    list: MyMysteryBoxItem[];
    pagination: MyMysteryBoxPagination;
    error: string | null;
    fetchList: (params: FetchParams, locale: string) => Promise<void>;
    loadMore: (locale: string) => Promise<void>;
}

const DEFAULT_PAGINATION: MyMysteryBoxPagination = {
    page: 1,
    size: 20,
    total: 0,
    hasNext: false,
};

/**
 * 我的盲盒列表，支持分页与加载更多
 */
export const useMyMysteryBox = (): UseMyMysteryBoxReturn => {
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState<MyMysteryBoxItem[]>([]);
    const [pagination, setPagination] = useState<MyMysteryBoxPagination>(DEFAULT_PAGINATION);
    const [error, setError] = useState<string | null>(null);

    // 保存最近一次请求参数，供 loadMore 复用
    const [lastParams, setLastParams] = useState<FetchParams>({});

    const fetchList = async (params: FetchParams, locale: string): Promise<void> => {
        setLoading(true);
        setError(null);
        const { page = 1, size = 20, userId, campaignId } = params;
        setLastParams({ userId, size, campaignId });
        try {
            const res = await getMyMysteryBoxList({ page, size, userId, campaignId }, locale);
            if (res?.code === '0') {
                const { content = [], total, hasNext } = res.data ?? {};
                setList(page === 1 ? content : (prev) => [...prev, ...content]);
                setPagination({ page, size, total, hasNext });
            } else {
                setError(res?.message ?? 'Failed to load mystery boxes');
            }
        } catch (err: any) {
            setError(err?.message ?? 'Failed to load mystery boxes');
        } finally {
            setLoading(false);
        }
    };

    const loadMore = async (locale: string): Promise<void> => {
        if (!pagination.hasNext || loading) return;
        await fetchList({ ...lastParams, page: pagination.page + 1 }, locale);
    };

    return { loading, list, pagination, error, fetchList, loadMore };
};
